// listPanel.js
// Provides a floating, scrollable panel listing i18n debug items that are not visibly tagged
// (e.g., off-screen aria/alt elements or wrappers outside the viewport).

let panelEl
let listEl
let toggleBtn
let initialized = false
let panelVisible = false

function createPanel() {
  panelEl = document.createElement('div')
  panelEl.className = 'i18n-key-list-panel'
  panelEl.setAttribute('role', 'complementary')
  panelEl.setAttribute('aria-label', 'i18n off-screen keys')
  const header = document.createElement('div')
  header.className = 'i18n-key-list-header'
  header.textContent = 'i18n Off‑Screen / Hidden'
  listEl = document.createElement('ul')
  listEl.className = 'i18n-key-list'
  panelEl.appendChild(header)
  panelEl.appendChild(listEl)
  panelEl.style.display = 'none'
  document.body.appendChild(panelEl)
  createToggleButton()
  initialized = true
}

function createToggleButton() {
  if (toggleBtn) return
  toggleBtn = document.createElement('button')
  toggleBtn.type = 'button'
  toggleBtn.className = 'i18n-panel-toggle-btn'
  toggleBtn.setAttribute('aria-label', 'Toggle i18n off-screen list')
  toggleBtn.setAttribute('aria-pressed', 'false')
  toggleBtn.textContent = 'i18n'
  toggleBtn.addEventListener('click', togglePanel)
  toggleBtn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      togglePanel()
    }
  })
  document.body.appendChild(toggleBtn)
}

function togglePanel() {
  panelVisible = !panelVisible
  if (panelEl) panelEl.style.display = panelVisible ? 'flex' : 'none'
  if (toggleBtn) toggleBtn.setAttribute('aria-pressed', panelVisible ? 'true' : 'false')
}

export function ensureListPanel() {
  if (initialized && panelEl && document.body.contains(panelEl)) return
  createPanel()
}

export function updateListPanel(items) {
  if (!initialized) ensureListPanel()
  if (!listEl) return
  // Stable key for diffing: use label + index fallback
  listEl.innerHTML = ''
  if (!items?.length) {
    const li = document.createElement('li')
    li.className = 'i18n-key-list-empty'
    li.textContent = 'No off-screen red/orange keys'
    listEl.appendChild(li)
    return
  }
  items.forEach((item) => {
    const { key, value, type, el } = item
    const li = document.createElement('li')
    li.className = 'i18n-key-list-item'
    li.setAttribute('data-type', type)
    li.innerHTML = `
      <span class="i18n-li-key">${escapeHtml(key)}</span>
      <span class="i18n-li-val">${escapeHtml(value)}</span>
    `
    li.tabIndex = 0
    li.addEventListener('click', () => focusTarget(el))
    li.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        focusTarget(el)
      }
    })
    listEl.appendChild(li)
  })
}

function focusTarget(el) {
  if (!el || !document.body.contains(el)) return
  // Scroll into view smoothly; highlight briefly
  if (typeof el.scrollIntoView === 'function') {
    // Use feature detection instead of try/catch; older browsers may not support smooth behavior
    const supportsSmooth = typeof document !== 'undefined' && 'scrollBehavior' in document.documentElement.style
    if (supportsSmooth) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    } else {
      el.scrollIntoView()
    }
  }
  el.classList.add('i18n-key-highlight')
  setTimeout(() => el.classList.remove('i18n-key-highlight'), 1800)
  if (typeof el.focus === 'function') {
    el.setAttribute('tabindex', '-1')
    el.focus({ preventScroll: true })
  }
}

// Expose for debugging
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__i18nListPanel = { ensureListPanel, updateListPanel }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Optional: expose toggle for manual console control
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__toggleI18nPanel = togglePanel
}
